import {
    FunctionComponent,
    useState,
    useCallback,
    useRef,
    useEffect
} from 'react';
import { Collapse } from '@mui/material';
import { PlanningQuestion, SubmitAnswerItem } from 'src/types/planning';
import { Answer, FREE_TEXT_OPTION, initAnswers } from './types';
import QuestionsHeader from './QuestionsHeader';
import QuestionsList from './QuestionsList';
import { SectionRoot } from '../../styled';

interface Props {
    questions: PlanningQuestion[];
    onSubmit: (answers: SubmitAnswerItem[]) => void;
    collapsible?: boolean;
}

const PlanningQuestions: FunctionComponent<Props> = ({
    questions,
    onSubmit,
    collapsible = false
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(false);

    // Scroll to top only when the number of questions changes after the initial
    // mount — avoids stealing focus on every re-render caused by selector
    // returning a new array reference with identical contents.
    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [questions.length]);

    // Section-level collapse (only relevant when collapsible=true)
    const [expanded, setExpanded] = useState(true);
    // Which individual question cards are open
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(
        new Set(questions.map((_, idx) => idx))
    );
    // Which questions the user chose to skip
    const [skipped, setSkipped] = useState<Set<number>>(new Set());
    // Unified answer state — works for both single-select and multi-select questions
    const [answers, setAnswers] = useState<Record<number, Answer>>(() =>
        initAnswers(questions)
    );

    const handleToggleExpanded = useCallback(
        () => setExpanded((prev) => !prev),
        []
    );

    // Expanding a collapsed question also un-skips it, since the user is re-engaging with it.
    const toggleQuestion = useCallback(
        (idx: number) => {
            setExpandedQuestions((prev) => {
                const next = new Set(prev);
                if (next.has(idx)) next.delete(idx);
                else next.add(idx);
                return next;
            });
            if (skipped.has(idx)) {
                setSkipped((prev) => {
                    const next = new Set(prev);
                    next.delete(idx);
                    return next;
                });
            }
        },
        [skipped]
    );

    const handleSkip = useCallback((idx: number) => {
        setSkipped((prev) => {
            const next = new Set(prev);
            next.add(idx);
            return next;
        });
        // Collapse the card after skipping so the list stays compact
        setExpandedQuestions((prev) => {
            const next = new Set(prev);
            next.delete(idx);
            return next;
        });
    }, []);

    // For single-select questions, replace the entire selection with the new value
    // so that at most one option is ever active (mirrors radio-button semantics).
    const handleSingleChange = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [qIdx]: { ...prev[qIdx], selected: new Set([value]) }
        }));
    }, []);

    // For multi-select, toggle the value in the set (add if absent, remove if present).
    const handleMultiToggle = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => {
            const current = new Set(prev[qIdx].selected);
            if (current.has(value)) current.delete(value);
            else current.add(value);
            return { ...prev, [qIdx]: { ...prev[qIdx], selected: current } };
        });
    }, []);

    const handleFreeTextChange = useCallback((qIdx: number, value: string) => {
        setAnswers((prev) => ({
            ...prev,
            [qIdx]: { ...prev[qIdx], freeText: value }
        }));
    }, []);

    const handleSubmit = useCallback(() => {
        const formatted: SubmitAnswerItem[] = questions.map((_, idx) => {
            const answer = answers[idx];

            // Strip the sentinel from the option IDs — the free-text content is
            // sent separately in the `freeText` field.
            const selectedOptionIds = Array.from(answer.selected).filter(
                (v) => v !== FREE_TEXT_OPTION
            );

            // Only include freeText when the user actually selected "Other".
            const freeText = answer.selected.has(FREE_TEXT_OPTION)
                ? answer.freeText
                : null;

            return { questionId: String(idx), selectedOptionIds, freeText };
        });
        onSubmit(formatted);
    }, [questions, answers, onSubmit]);

    return (
        <SectionRoot ref={rootRef}>
            <QuestionsHeader
                questions={questions}
                collapsible={collapsible}
                expanded={expanded}
                onToggleExpanded={handleToggleExpanded}
            />
            <Collapse in={!collapsible || expanded}>
                <QuestionsList
                    questions={questions}
                    answers={answers}
                    expandedQuestions={expandedQuestions}
                    skipped={skipped}
                    onToggleQuestion={toggleQuestion}
                    onSkip={handleSkip}
                    onSingleChange={handleSingleChange}
                    onMultiToggle={handleMultiToggle}
                    onFreeTextChange={handleFreeTextChange}
                    onSubmit={handleSubmit}
                />
            </Collapse>
        </SectionRoot>
    );
};

export default PlanningQuestions;
